import { useState, type JSX } from 'react'
import type { ToastActionProps } from './types'
import { useLink } from '@/hooks/useLink'
import { cn } from '@ttab/elephant-ui/utils'
import { useToastClick } from './hooks/useToastClick'

export { withToastActions } from './lib/withToastActions'
export type { ToastActionProps } from './types'

/**
 * Single toast shortcut. The icon is the button; a click anywhere else in the toast also
 * triggers it, except in error toasts (see isToastClick).
 */
export const ToastAction = ({
  documentId,
  planningId,
  withView,
  Icon,
  label,
  target = 'last'
}: ToastActionProps): JSX.Element | null => {
  const openDocument = useLink(withView)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)

  useToastClick(anchor, (event) => {
    if (documentId) {
      openDocument(event, { id: documentId, planningId }, target)
    }
  })

  if (!documentId) {
    return null
  }

  // Colors follow the toast (currentColor), whose theme may differ from the app's
  return (
    <button
      ref={setAnchor}
      type='button'
      data-toast-action
      aria-label={label}
      title={label}
      className={cn(
        'ml-auto flex h-10 w-12 shrink-0 items-center justify-center rounded-md cursor-pointer',
        'opacity-60 transition-opacity hover:opacity-100',
        '[[data-sonner-toast]:not([data-type=error]):hover_&]:opacity-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
      onClick={(event) => openDocument(event, { id: documentId, planningId }, target)}
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden />
    </button>
  )
}
